<template>
	<div v-if="isShown" class="mt-6">
		<p v-if="showTitle" class="text-center font-medium text-gray-700">
			Choose your plan
		</p>
		<div class="mt-4 flex flex-col gap-2">
			<div class="flex flex-col gap-2">
				<div
					v-for="(plan, contractKey, index) in plans"
					:key="contractKey"
				>
					<SelectableCardRadioButton
						:subheadline="getSubheadline(plan, contractKey)"
						:headline="getHeadline(plan, contractKey)"
						:description="getDescription(plan, contractKey)"
						:is-selected="isPlanSelected(plan, index)"
						:footnote-title="
							contractKey === 'subs_12m_plan' ? '2' : ''
						"
						@on-selected="
							getSelected(plan?.contract?.period_length ?? null)
						"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import SelectableCardRadioButton from '@/components/buttons/SelectableCardRadioButton.vue'
import { useProductDetails } from '@/stores/product-details'

const productDetails = useProductDetails()

interface SubscriptionPlan {
	name?: string
	access_fee?: number | null
	contract?: {
		period_length?: number | null
	} | null
	discount?: {
		info?: string
		rate?: string | number
	} | null
}

interface SubscriptionPlansProps {
	modelValue?: number | null
	plans: Record<string, SubscriptionPlan>
	showTitle?: boolean
	showSinglePlan?: boolean
	showPrice?: boolean
}

const props = withDefaults(defineProps<SubscriptionPlansProps>(), {
	modelValue: null,
	showTitle: true,
	showSinglePlan: false,
	showPrice: false,
})

const emit = defineEmits<{
	(e: 'update:modelValue' | 'selectedPlan', value: number | null): void
}>()

const isPlanSelected = (plan: SubscriptionPlan, index: number): boolean => {
	const planPeriod = plan?.contract?.period_length ?? null

	// If modelValue matches the period (e.g., 12 === 12)
	if (props.modelValue === planPeriod) {
		return true
	}

	// Default fallback: select index 0 if modelValue is undefined/unmatched
	if (props.modelValue === null && index === 0) {
		return true
	}

	return false
}

const getSelected = (periodLength: number | null) => {
	productDetails.setSubscriptionDuration(periodLength)
	emit('update:modelValue', periodLength)
	emit('selectedPlan', periodLength)
}

// Hide standard plan if it is the only plan available.
const isShown = computed(() => {
	if (
		!props.showSinglePlan &&
		Object.keys(props.plans).length === 1 &&
		props.plans.flexi_plan &&
		props.plans.flexi_plan?.name === 'Standard subscription'
	) {
		return false
	}

	return true
})

// With the weekly price as the headline, the plan's name moves up to the subheadline
const hasPrice = (plan: SubscriptionPlan) =>
	props.showPrice && plan.access_fee != null

const getSubheadline = (plan: SubscriptionPlan, contractKey: string) =>
	hasPrice(plan) ? getPlanName(plan, contractKey) : ''

const getHeadline = (plan: SubscriptionPlan, contractKey: string) =>
	hasPrice(plan)
		? `${formatCurrency(plan.access_fee)} per week`
		: getPlanName(plan, contractKey)

const getPlanName = (plan: SubscriptionPlan, contractKey: string) => {
	switch (contractKey) {
		case 'flexi_plan':
			return plan.name || 'Standard subscription'
		case 'subs_12m_plan':
			return plan.name || 'Minimum term 12 months'
		default:
			return `${plan?.contract?.period_length} months`
	}
}

const getDescription = (plan: SubscriptionPlan, contractKey: string) => {
	if (props.plans?.min_term_12?.discount) return ''

	switch (contractKey) {
		case 'flexi_plan':
			return 'Flexible - Stop or swap with 2 weeks notice.'
		case 'subs_12m_plan':
			return plan?.discount?.info || ''
		default:
			return plan?.discount?.rate ? '' : plan?.discount?.info || ''
	}
}
</script>
